import diagnostics from "@/services/diagnostics";
import { createRouter, createWebHistory } from "vue-router";

import MainScreen from "./views/MainScreen.vue";
import AgreementScreen from "./views/AgreementScreen.vue";
import SearchScreen from "./views/SearchScreen.vue";
import GameScreen from "./views/GameScreen.vue";
import ResultScreen from "./views/ResultScreen.vue";
import ReplayScreen from "./views/ReplayScreen.vue";
import RewardScreen from "./views/RewardScreen.vue";
import LeaderboardScreen from "./views/LeaderboardScreen.vue";
import ProfileScreen from "./views/ProfileScreen.vue";
import RulesScreen from "./views/RulesScreen.vue";
import RoomScreen from "./views/RoomScreen.vue";
import { useRoomStore } from "./stores/room";
import CriticalScreen from "./views/CriticalScreen.vue";

import { useGameStore } from "./stores/game";
import { useSessionStore } from "./stores/session";
import realtime from "./services/realtime";
import telegram from "./services/telegram";
import { criticalState } from "./services/critical";

/**
 * Маршруты (CONTRACT §6).
 * meta:
 *   requiresAgreement — нужно принятое соглашение, иначе /agreement?next=search (поиск открывает камеру)
 *   requiresGame   — нужен game.uuid, иначе редирект на «/»
 *   requiresResult — нужен game.uuid и game.result, иначе «/»
 *   back           — куда ведёт системная кнопка Telegram «Назад» (false — кнопка скрыта)
 *   inGame         — включить подтверждение закрытия Mini App
 */
const routes = [
  { path: "/", name: "main", component: MainScreen, meta: { back: false } },
  { path: "/agreement", name: "agreement", component: AgreementScreen, meta: { back: "/" } },
  { path: "/room", name: "room", component: RoomScreen, meta: { requiresAgreement: true, back: "/", inGame: true } },
  { path: "/search", name: "search", component: SearchScreen, meta: { requiresAgreement: true, back: "/" } },
  { path: "/game", name: "game", component: GameScreen, meta: { requiresGame: true, back: false, inGame: true } },
  { path: "/result", name: "result", component: ResultScreen, meta: { requiresResult: true, back: "/" } },
  { path: "/replay", name: "replay", component: ReplayScreen, meta: { requiresResult: true, back: "/" } },
  { path: "/reward", name: "reward", component: RewardScreen, meta: { requiresResult: true, back: "/" } },
  { path: "/leaderboard", name: "leaderboard", component: LeaderboardScreen, meta: { back: "/" } },
  { path: "/profile", name: "profile", component: ProfileScreen, meta: { back: "/" } },
  { path: "/diagnostics", name: "diagnostics", component: () => import(/* webpackChunkName: "diagnostics-screen" */ "./views/DiagnosticsScreen.vue"), meta: { back: "/profile" } },
  { path: "/rules", name: "rules", component: RulesScreen, meta: { back: "back" } },
  { path: "/critical-modal", name: "critical", component: CriticalScreen, meta: { back: "/" } },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach((to) => {
  const game = useGameStore();
  if (to.meta.requiresAgreement && !useSessionStore().agreementAccepted) {
    return { path: "/agreement", query: { next: to.name === "room" ? "room" : "search", ...(to.name === "room" && to.query.invite ? { invite: to.query.invite } : {}) }, replace: true };
  }
  if (to.meta.requiresGame && !game.hasGame) return { path: "/", replace: true };
  if (to.meta.requiresResult && !game.hasResult) return { path: "/", replace: true };
  if (to.name === "critical" && !criticalState.active) return { path: "/", replace: true };
  return true;
});

const ON_MAIN_SCREEN = { type: "page_change", page: "ON_MAIN_SCREEN" };

router.afterEach((to, from, failure) => {
  if (failure) return;
  diagnostics.setScreen(to.name);

  // Главный экран: сервер отменяет незавершённую игру игрока. Не ставим в очередь —
  // при (пере)подключении сообщение отправит обработчик 'open' ниже, если мы всё ещё на «/».
  if (to.path === "/" && !useRoomStore().inviteToken) realtime.send(ON_MAIN_SCREEN, { queue: false });

  // Системная кнопка «Назад» Telegram. Экран может переопределить обработчик в onMounted:
  // telegram.backButton(true, myHandler).
  const back = to.meta.back;
  if (back) {
    telegram.backButton(true, () => {
      if (back === "back" && window.history.state && window.history.state.back) router.back();
      else router.push(back === "back" ? "/" : back);
    });
  } else {
    telegram.backButton(false);
  }

  telegram.closingConfirmation(!!to.meta.inGame);
});

// Первое подключение и каждое переподключение: если пользователь на «/», сообщаем серверу.
realtime.on("open", () => {
  if (router.currentRoute.value.path === "/" && !useRoomStore().inviteToken) realtime.send(ON_MAIN_SCREEN, { queue: false });
});

export default router;
